import React, {
  useMemo,
  useEffect,
  useState,
  ComponentType,
  PropsWithChildren,
} from 'react';

import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { useGetAllPaginatedData, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  useContextWithValidation,
  getOrCreateGlobalContext,
} from '@adsk/alloy-react-context';

import { useCurrentProjectId } from './CurrentProjectIdContext';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';
import useClientSideDataModification from './hooks/useClientSideDataModification';
import { Company, Pagination } from './types';

type CompaniesResponse = {
  pagination: Pagination;
  results: Company[];
};

type CompaniesContextValue = {
  data: Company[];
  loading: boolean;
  error?: null | Error;
  retry(): void;
  addCompanies(companies: Company[]): void;
  updateCompany(company: Company): void;
  removeCompanyById(id: Company['id']): void;
  setEnabled?: (enabled: boolean) => void;
};

/**
 * Context for companies list.
 */
const CompaniesContext = getOrCreateGlobalContext<CompaniesContextValue>(
  CONTEXT_NAMES.CompaniesContext,
);

const useCompaniesContext = () =>
  useContextWithValidation(CompaniesContext, {
    hook: 'useCompanies',
    provider: 'CompaniesProvider',
  });

export const useCompanies = () => {
  const { setEnabled, ...rest } = useCompaniesContext();
  useEffect(() => {
    if (setEnabled) {
      setEnabled(true);
    }
  }, [setEnabled]);
  return rest;
};

const PAGE_LIMIT = 200;

export const CompaniesProvider: ComponentType<PropsWithChildren> = ({
  children,
}) => {
  const [enabled, setEnabled] = useState(false);
  const projectId = useCurrentProjectId();
  const adminApiUrl = getAdminApiUrl();
  const url =
    enabled && projectId
      ? `${adminApiUrl}/v1/projects/${projectId}/companies?limit=${PAGE_LIMIT}`
      : '';

  const {
    loading: loadingData,
    error,
    data: dataFromBE,
    retry,
    clearSessionStorage,
  } = useGetAllPaginatedData<CompaniesResponse>({
    url,
  });

  const companiesFromBE = useMemo(() => {
    return (
      dataFromBE &&
      removeOldDuplicatesBy(
        dataFromBE.flatMap((page) => page.results),
        (item: Company) => item.id,
      )
    );
  }, [dataFromBE]);

  const {
    modifyData,
    addData: addCompanies,
    updateEntity: updateCompany,
    removeEntityById: removeCompanyById,
  } = useClientSideDataModification<Company>(clearSessionStorage);

  const loading = loadingData || !enabled;
  const data = useMemo(
    () => modifyData(companiesFromBE),
    [modifyData, companiesFromBE],
  );

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
      addCompanies,
      updateCompany,
      removeCompanyById,
      setEnabled: enabled ? undefined : setEnabled,
    }),
    [
      addCompanies,
      updateCompany,
      data,
      error,
      loading,
      removeCompanyById,
      retry,
      enabled,
    ],
  );

  return (
    <CompaniesContext.Provider value={value}>
      {children}
    </CompaniesContext.Provider>
  );
};

CompaniesProvider.displayName = 'CompaniesProvider';
